import type { CaptionLines } from './placement';
/** A recognised word with source-time bounds (seconds). */
export interface CaptionWord {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/** One on-screen caption line (source time). `words` drives word highlighting. */
export interface CaptionCue {
  readonly id: string;
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly words: readonly CaptionWord[];
}

/** Speech model size: `fast` downloads less, `accurate` transcribes noticeably better. */
export type AsrQuality = 'fast' | 'accurate';

/** Approximate one-time model downloads, for UI copy. */
export const ASR_DOWNLOAD_LABELS: Readonly<Record<AsrQuality, string>> = { fast: '100 MB', accurate: '300 MB' };

/** Languages offered in the UI (Whisper codes); `auto` lets the model detect it. */
export const CAPTION_LANGUAGES = ['auto', 'tr', 'en', 'de', 'fr', 'es', 'it', 'pt', 'ru', 'ar', 'nl'] as const;
export type CaptionLanguage = (typeof CAPTION_LANGUAGES)[number];

/**
 * What the captions say: the words as spoken, or (Whisper's own `translate`
 * task) an English translation of them. Writing as spoken is the default.
 */
export type CaptionTask = 'transcribe' | 'translate';
export const CAPTION_TASKS: readonly CaptionTask[] = ['transcribe', 'translate'];

/** The one language Whisper translates into. */
export const TRANSLATION_LANGUAGE = 'en';

/** The task Whisper runs for speech in `language`: English speech has nothing to translate into English. */
export const whisperTask = (task: CaptionTask, language: string): CaptionTask => (task === 'translate' && language !== TRANSLATION_LANGUAGE ? 'translate' : 'transcribe');

export type CaptionStyleId = 'classic' | 'pop' | 'box' | 'minimal';
export const CAPTION_STYLES: readonly CaptionStyleId[] = ['pop', 'classic', 'box', 'minimal'];

export type CaptionPosition = 'top' | 'middle' | 'bottom';
export const CAPTION_POSITIONS: readonly CaptionPosition[] = ['top', 'middle', 'bottom'];

export interface CaptionSettings {
  /** Burn captions into the video. */
  readonly enabled: boolean;
  readonly cues: readonly CaptionCue[];
  readonly style: CaptionStyleId;
  readonly position: CaptionPosition;
  /**
   * Where the person dragged the captions to: the block's centre as 0..1 of
   * the output frame. Overrides `position` until a position is picked again.
   */
  readonly anchor: { readonly x: number; readonly y: number } | null;
  /** 0..1 relative text size. */
  readonly size: number;
  /**
   * Set when the cues are an English translation: the language that was
   * spoken (Whisper code). Their words then aren't what was said, so nothing
   * that edits by the spoken words (fillers, the script) reads them.
   */
  readonly translatedFrom?: string;
  /**
   * Where the preset positions sit, for a video fitted for a place. Only the
   * copy a render gets carries it (`placedCaptions`); a saved recipe never does.
   */
  readonly lines?: CaptionLines;
}

export const DEFAULT_CAPTION_SETTINGS: CaptionSettings = { enabled: false, cues: [], style: 'pop', position: 'bottom', anchor: null, size: 0.5 };

/** The cues as spoken words: none when they are a translation (their words weren't said). */
export const spokenCues = (captions: Pick<CaptionSettings, 'cues' | 'translatedFrom'>): readonly CaptionCue[] => (captions.translatedFrom ? [] : captions.cues);

